import React, { Suspense, lazy } from "react";
import { withChunkReloadGuard } from "../../lib/chunkReloadGuard";

/**
 * Code-split entry point for {@link RichTextEditor}.
 *
 * The editor pulls TipTap and ProseMirror in with it — the single largest
 * dependency in the bundle — and every call site renders it only after the
 * user clicks into a description. Loading it on demand keeps that weight out
 * of the initial payload; import this module rather than `RichTextEditor`
 * directly, or the split is undone.
 */
const RichTextEditor = lazy(
  withChunkReloadGuard("richTextEditorChunkReloaded", () =>
    import("./RichTextEditor"),
  ),
);

type RichTextEditorProps = React.ComponentProps<typeof RichTextEditor>;

/**
 * Placeholder that matches the editor's frame — toolbar strip above, content
 * area below — so the surrounding layout doesn't shift when the chunk lands.
 */
const EditorFallback: React.FC = () => (
  <div className="bg-warm-bg-elevated border border-warm-border rounded-lg overflow-hidden">
    <div className="h-[33px] border-b border-warm-border bg-warm-border-subtle" />
    <div className="min-h-[120px]" />
  </div>
);

const LazyRichTextEditor: React.FC<RichTextEditorProps> = (props) => (
  <Suspense fallback={<EditorFallback />}>
    <RichTextEditor {...props} />
  </Suspense>
);

export default LazyRichTextEditor;
